import {
  EuiButton,
  EuiCallOut,
  EuiCheckableCard,
  EuiEmptyPrompt,
  EuiFormFieldset,
  EuiIcon,
  EuiLoadingChart,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTextColor,
  EuiTitle,
} from "@inscopix/ideas-eui";
import { useFormikContext } from "formik";
import { ToolEditorPublishValues } from "./ToolEditorPublish";
import { useCallback, useEffect, useState } from "react";
import { checkToolVersionDraftCompatibility } from "components/ToolEditor/api/checkToolVersionDraftCompatibility";
import { captureException } from "@sentry/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";

export interface PublishOptionsExistingVersionProps {
  options: { major: string; minor: string; patch: string };
}

export const PublishOptionsExistingVersion = ({
  options,
}: PublishOptionsExistingVersionProps) => {
  const toolVersionId = useToolEditorContext((s) => s.toolVersionId);
  const { setFieldValue, values } = useFormikContext<ToolEditorPublishValues>();
  const setIsPublishActionEnabled = useToolEditorContext(
    (s) => s.setIsPublishActionEnabled,
  );

  const [compatibility, setCompatibility] = useState<
    "loading" | "error" | "compatible" | "incompatible"
  >("loading");

  const checkDraftCompatibility = useCallback(async () => {
    setCompatibility("loading");
    setIsPublishActionEnabled(false);
    try {
      const response = await checkToolVersionDraftCompatibility(toolVersionId);
      if (response.compatibility === "incompatible") {
        setCompatibility("incompatible");
        void setFieldValue("version", "major");
      } else {
        setCompatibility("compatible");
      }
      setIsPublishActionEnabled(true);
    } catch (error) {
      setIsPublishActionEnabled(false);

      setCompatibility("error");
      addUtilityToastFailure(
        "Failed to check tool version draft compatibility.",
      );
      captureException(error);
    }
  }, [setFieldValue, setIsPublishActionEnabled, toolVersionId]);

  // when component loads, check draft version compatibility
  useEffect(() => {
    void checkDraftCompatibility();
  }, [checkDraftCompatibility]);

  const selectedOption = values.version;
  const newVersion =
    selectedOption === "first_version" ? "1.0.0" : options[selectedOption];
  const selectedOptionDisplay = (() => {
    switch (selectedOption) {
      case "major":
        return "Major";
      case "minor":
        return "Minor";
      case "patch":
        return "Patch";
      default:
        return "";
    }
  })();

  if (compatibility === "loading") {
    return (
      <EuiEmptyPrompt
        icon={<EuiLoadingChart size="xl" mono />}
        title={<h2>Checking version compatibility...</h2>}
        titleSize="xs"
      />
    );
  }

  // don't show options if there was an error checking draft compatibility
  // allow the user to trigger a retry
  if (compatibility === "error") {
    return (
      <EuiCallOut
        color="danger"
        title="An error occurred while checking draft version compatibility."
      >
        <EuiButton
          size="s"
          iconType={"refresh"}
          onClick={() => void checkDraftCompatibility()}
          fill
        >
          Retry
        </EuiButton>
      </EuiCallOut>
    );
  }

  return (
    <>
      <EuiFormFieldset
        legend={{
          children: (
            <EuiTitle size="xxs">
              <span>
                Tool version numbers in IDEAS communicate whether or not a tool
                version is compatible with the previous versions - i.e. whether
                it should be usable within the same analysis table or not. What
                kind of change is this?
              </span>
            </EuiTitle>
          ),
        }}
      >
        <EuiCheckableCard
          id={"major"}
          label={
            <EuiText>
              <strong>Major: </strong>
              This version includes changes to the table and is not compatible
              with previous versions, or it includes significant code changes
              that may affect results to a degree that it should not be used in
              the same analysis table as the previous version.
              {compatibility === "incompatible" && (
                <EuiCallOut
                  size="s"
                  color="primary"
                  iconType="iInCircle"
                  title="The draft version includes changes that are not compatible with the latest published version, so only a Major version can be selected."
                />
              )}
            </EuiText>
          }
          name={"versionGroup"}
          value="Major"
          checked={selectedOption === "major"}
          onChange={() => void setFieldValue("version", "major")}
        />

        <EuiSpacer size="m" />

        <EuiCheckableCard
          id={"minor_option"}
          label={
            <EuiText>
              <strong>Minor: </strong>
              This version is compatible with previous version and can be used
              in the same analysis tables, but it includes changes or fixes that
              may change results.
            </EuiText>
          }
          name={"versionGroup"}
          value="Minor"
          checked={selectedOption === "minor"}
          onChange={() => void setFieldValue("version", "minor")}
          disabled={compatibility === "incompatible"}
        />

        <EuiSpacer size="m" />

        <EuiCheckableCard
          id={"patch_option"}
          label={
            <EuiText>
              <strong>Patch: </strong>
              This version only contains minor changes that do not affect
              results (e.g. documentation updates, parameter name changes, etc).
            </EuiText>
          }
          name={"versionGroup"}
          value="Patch"
          checked={selectedOption === "patch"}
          onChange={() => void setFieldValue("version", "patch")}
          disabled={compatibility === "incompatible"}
        />
      </EuiFormFieldset>
      <EuiSpacer size="m" />
      <EuiStat
        title={newVersion}
        description={
          <EuiTextColor color="success">
            <span>
              <EuiIcon type="sortUp" /> {selectedOptionDisplay}
            </span>
          </EuiTextColor>
        }
        textAlign="left"
      ></EuiStat>
    </>
  );
};
